import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchServices } from '../lib/cms'
import { useDocumentMeta } from '../hooks/useDocumentMeta'

export default function Services() {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeFaq, setActiveDropdown] = useState(null)

  useDocumentMeta({
    title: 'Ydelser | Psykoterapi, Parterapi & Familieterapi i Svendborg',
    description: 'Søger du psykoterapeut i Svendborg? Se oversigten over ydelser: Psykoterapi Svendborg, parterapi, familieterapi og supervision for hele Fyn.',
    image: 'https://assets.auxodev.dk/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/psychotherapy-man-scaled.jpg'
  })

  useEffect(() => {
    async function loadServices() {
      const data = await fetchServices()
      setServices(data)
      setLoading(false)
    }
    loadServices()
  }, [])

  const faqs = [
    {
      q: 'Hvad betyder det, at du er integrativ psykoterapeut?',
      a: 'At være integrativ betyder, at jeg ikke binder mig til en enkelt terapeutisk metode. I stedet tilpasser jeg samtalerne og metoderne til dit unikke behov og din livssituation med afsæt i både kognitive, oplevelsesorienterede og kropslige indgangsvinkler.'
    },
    {
      q: 'Hvordan foregår et forløb som familiebehandler?',
      a: 'I familiebehandlingen mødes vi om familiens samlede trivsel. Vi afdækker de mønstre og dynamikker, der skaber konflikter eller afstand, og finder konkrete redskaber til at forbedre kommunikationen og støtte børnenes tryghed.'
    },
    {
      q: 'Hvad er din rolle som samværskonsulent?',
      a: 'Som samværskonsulent er min primære opgave at sikre barnets tryghed og trivsel under samvær med en forælder. Det kan indebære støttet eller overvåget samvær, observation og børnesagkyndig rådgivning.'
    },
    {
      q: 'Hvem tilbyder du supervision til?',
      a: 'Jeg tilbyder supervision til både individuelle fagpersoner og personalegrupper – herunder psykoterapeuter, pædagoger, lærere og sundhedsfagligt personale, der har brug for faglig sparring, refleksion og forebyggelse af belastning.'
    },
    {
      q: 'Hvor foregår samtalerne og møderne?',
      a: 'Samtaler, parterapi i Svendborg og familieterapi afholdes i min rolige klinik på Dronningemaen 68 i Svendborg på Sydfyn. Klinikken ligger tæt på Svendborg Station og med gode parkeringsforhold, så det er let at komme til terapi på Fyn, uanset om du kommer fra lokalområdet, Faaborg, Nyborg eller Odense. Efter aftale tilbydes også onlineterapi samt eksterne indsatser og supervision.'
    }
  ]

  return (
    <div className="pt-[72px] min-h-screen bg-brand-offwhite">
      {/* Hero Section */}
      <section className="bg-brand-sand/20 py-16 border-b border-brand-sand/30">
        <div className="max-w-5xl mx-auto px-6 text-center flex flex-col gap-4">
          <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-semibold">
            Faglige Arbejdsområder & Myndighedsopgaver
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-brand-dark">
            Ydelser & Myndighedsopgaver
          </h1>
          <p className="text-sm sm:text-base text-brand-gray/70 max-w-2xl mx-auto">
            Integrativ psykoterapeut i Svendborg · Parterapi · Familieterapi · Terapi på Fyn
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-10 text-center max-w-2xl mx-auto">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-brand-dark">Hovedområder i praksis</h2>
            <p className="text-sm text-brand-gray/70 mt-2">
              Læs mere om mine primære faglige indsatser i Svendborg for private, par, familier og fagpersoner på Fyn.
            </p>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-dark" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-white border border-brand-sand/30 rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col group"
                >
                  {/* Service Image */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-brand-sand/20">
                    <img
                      src={service.hero_billede?.[0] ? `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${service.hero_billede[0].replace('https://assets.auxodev.dk/', '')}` : 'https://assets.auxodev.dk/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/danish-therapy-room.jpg'}
                      alt={service.titel}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/40 via-transparent to-transparent" />
                  </div>

                  {/* Service Content */}
                  <div className="p-8 flex flex-col flex-grow gap-4">
                    <h2 className="font-serif text-xl font-bold text-brand-dark group-hover:text-brand-sage transition-colors duration-200">
                      {service.titel}
                    </h2>
                    <p className="text-sm text-brand-gray/70 leading-relaxed line-clamp-3">
                      {service.kort_beskrivelse}
                    </p>
                    <Link
                      to={`/ydelser/${service.url_slug}`}
                      className="text-xs font-semibold uppercase tracking-wider text-brand-dark mt-auto flex items-center gap-1.5 group-hover:text-brand-sage transition-colors duration-200"
                    >
                      {service.url_slug?.includes('parterapi') || service.titel?.toLowerCase().includes('parterapi')
                        ? 'Læs om parterapi'
                        : service.url_slug?.includes('integrativ') || service.titel?.toLowerCase().includes('individuel') || service.titel?.toLowerCase().includes('psykoterapeut')
                        ? 'Læs om individuel terapi'
                        : `Læs om ${service.titel.toLowerCase()}`}{' '}
                      <i className="fa-light fa-arrow-right text-[10px] transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Specialiserede Myndighedsopgaver Section */}
      <section className="py-20 bg-brand-sand/20 border-t border-b border-brand-sand/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center flex flex-col gap-4 mb-14 max-w-3xl mx-auto">
            <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-bold">
              Forvaltninger & Kommuner
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-dark">
              Specialiserede Myndighedsopgaver
            </h2>
            <div className="h-1 w-12 bg-brand-sage mx-auto rounded-full" />
            <p className="text-sm sm:text-base text-brand-gray/80 leading-relaxed">
              Jeg udfører opgaver som ekstern leverandør for kommuner og familieafdelinger med fokus på tryghed, faglig observation og det enkelte barns tarv.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Familiebehandling */}
            <div className="bg-white border border-brand-sand/40 rounded-3xl p-7 shadow-sm hover:shadow-md transition-all flex flex-col gap-3">
              <div className="w-12 h-12 rounded-2xl bg-brand-sand/30 text-brand-dark flex items-center justify-center text-xl">
                <i className="fa-solid fa-people-roof" />
              </div>
              <h3 className="font-serif text-lg font-bold text-brand-dark">Familiebehandling</h3>
              <p className="text-xs sm:text-sm text-brand-gray/70 leading-relaxed">
                Helhedsorienteret forløb med fokus på relationer, kommunikation og gensidig forståelse i sårbare eller udfordrede familier.
              </p>
            </div>

            {/* Samværskonsulent */}
            <div className="bg-white border border-brand-sand/40 rounded-3xl p-7 shadow-sm hover:shadow-md transition-all flex flex-col gap-3">
              <div className="w-12 h-12 rounded-2xl bg-brand-sand/30 text-brand-dark flex items-center justify-center text-xl">
                <i className="fa-solid fa-hands-holding-child" />
              </div>
              <h3 className="font-serif text-lg font-bold text-brand-dark">Samværskonsulent</h3>
              <p className="text-xs sm:text-sm text-brand-gray/70 leading-relaxed">
                Støttet og overvåget samvær mellem anbragte børn og biologiske forældre i rolige og trygge rammer med faglig dokumentation.
              </p>
            </div>

            {/* Støttekontaktperson */}
            <div className="bg-white border border-brand-sand/40 rounded-3xl p-7 shadow-sm hover:shadow-md transition-all flex flex-col gap-3">
              <div className="w-12 h-12 rounded-2xl bg-brand-sand/30 text-brand-dark flex items-center justify-center text-xl">
                <i className="fa-solid fa-person-walking" />
              </div>
              <h3 className="font-serif text-lg font-bold text-brand-dark">Støttekontaktperson</h3>
              <p className="text-xs sm:text-sm text-brand-gray/70 leading-relaxed">
                Individuel støtte og tryg relation til unge eller voksne, der har brug for støtte til livsmestring, uddannelse eller relationer.
              </p>
            </div>

            {/* Komplekse familieindsatser */}
            <div className="bg-white border border-brand-sand/40 rounded-3xl p-7 shadow-sm hover:shadow-md transition-all flex flex-col gap-3">
              <div className="w-12 h-12 rounded-2xl bg-brand-sand/30 text-brand-dark flex items-center justify-center text-xl">
                <i className="fa-solid fa-shield-heart" />
              </div>
              <h3 className="font-serif text-lg font-bold text-brand-dark">Komplekse familieindsatser</h3>
              <p className="text-xs sm:text-sm text-brand-gray/70 leading-relaxed">
                Håndtering af sammensatte og akutte problemstillinger, højt konfliktniveau eller flerstrengede kriser i familien.
              </p>
            </div>
          </div>

          <div className="mt-10 bg-white/80 border border-brand-sand/40 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-tag text-brand-sage text-2xl" />
              <div>
                <span className="font-serif font-bold text-brand-dark text-base block">Takst for myndighedsopgaver</span>
                <span className="text-xs sm:text-sm text-brand-gray/70">750 kr. pr. time (+ tillæg ved aften, weekender &amp; helligdage)</span>
              </div>
            </div>
            <Link
              to="/priser"
              className="bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-xs font-semibold uppercase tracking-wider px-6 py-3 rounded-full transition-all shrink-0"
            >
              Se priser
            </Link>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-brand-sand/10 border-t border-b border-brand-sand/30">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col gap-6">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark">
            Hvordan finder vi den rette indsats for dig?
          </h2>
          <div className="h-1 w-12 bg-brand-sage mx-auto rounded-full" />
          <p className="text-sm sm:text-base text-brand-gray/80 leading-relaxed">
            Hvad enten du søger individuel psykoterapi i Svendborg, parterapi, familieterapi på Fyn eller faglig supervision, starter vi altid med en indledende afklarende samtale. Her drøfter vi dine eller jeres ønsker, forventninger og mål for forløbet.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            >
              Book en samtale
            </Link>
            <Link
              to="/priser"
              className="bg-brand-offwhite border border-brand-dark/30 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
            >
              Se priser
            </Link>
            <Link
              to="/kontakt"
              className="bg-brand-sand/30 border border-brand-sand/70 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-brand-offwhite">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center flex flex-col gap-4 mb-12">
            <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-semibold">Spørgsmål & Svar</span>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark">Ofte stillede spørgsmål</h2>
          </div>

          <div className="flex flex-col gap-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="border border-brand-sand/40 rounded-xl overflow-hidden bg-brand-offwhite shadow-sm"
              >
                <button
                  onClick={() => setActiveDropdown(activeFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left font-serif font-semibold text-brand-dark hover:text-brand-sage transition-colors duration-150"
                >
                  <span>{faq.q}</span>
                  <i className={`fa-light fa-chevron-down text-sm transition-transform duration-200 ${activeFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {activeFaq === idx && (
                  <div className="px-6 pb-6 text-sm text-brand-gray/70 leading-relaxed border-t border-brand-sand/10 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
